/**
 * Las NECESIDADES que se resuelven en la lista ancha de /planos (adicionales de un concepto e
 * instalaciones que el plano no dibuja): cómo se reconocen por nombre, cómo crece la lista con
 * «Proponer más» y cuánto falta asignar cuando una necesidad se cubre con más de un concepto.
 *
 * Todo es comparación de TEXTO normalizado: no hay tablas de sinónimos ni de equivalencias de unidades
 * (CLAUDE.md §11). Si dos unidades no se escriben igual, no se restan: se avisa con las dos cantidades.
 */

/** Nombre de una necesidad para compararla: minúsculas, sin acentos, sin puntuación final, espacios simples. */
export function normalizarNecesidad(nombre: string): string {
  return nombre
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[\s.,;:]+$/, '')
    .trim();
}

/** Unidad para compararla: minúsculas, sin espacios ni puntos («M2.» = «m2»; «sal» ≠ «salida»). */
export function normalizarUnidadTexto(unidad: string): string {
  return unidad.toLowerCase().replace(/[\s.]/g, '');
}

/**
 * «Proponer más»: agrega al FINAL las necesidades nuevas que no estén ya (por nombre normalizado, ni
 * repetidas entre sí). Las de antes no se mueven: su posición, su ✓ y su «no va» siguen igual.
 * Devuelve la lista nueva y desde qué posición empiezan las agregadas.
 */
export function agregarNecesidadesNuevas<T>(
  actuales: readonly T[],
  nuevas: readonly T[],
  nombreDe: (t: T) => string,
): { lista: T[]; primeraNueva: number | null; agregadas: number } {
  const vistas = new Set(actuales.map((t) => normalizarNecesidad(nombreDe(t))));
  const lista = [...actuales];
  for (const t of nuevas) {
    const k = normalizarNecesidad(nombreDe(t));
    if (!k || vistas.has(k)) continue;
    vistas.add(k);
    lista.push(t);
  }
  const agregadas = lista.length - actuales.length;
  return { lista, primeraNueva: agregadas > 0 ? actuales.length : null, agregadas };
}

/** Lo ya asignado en la hoja a una necesidad: la cantidad de cada renglón y su unidad. */
export interface AsignadoANecesidad {
  cantidad: number;
  unidad: string | null;
}

/**
 * Una necesidad puede cubrirse con MÁS de un concepto (excavación tipo I + tipo II). Para no cobrar
 * doble, la cantidad que se propone al agregar otro concepto es lo que FALTA: `objetivo` − lo asignado
 * (mínimo 0). Solo se resta si TODO lo asignado está en la misma unidad (texto normalizado) que el
 * objetivo; si no, `faltante` = null y `mismaUnidad` = false (se avisa sin restar). Sin objetivo no hay
 * qué restar.
 */
export function cantidadQueFalta(
  objetivo: number | null,
  unidadObjetivo: string | null,
  asignado: readonly AsignadoANecesidad[],
): { asignado: number; faltante: number | null; mismaUnidad: boolean } {
  const total = asignado.reduce((s, a) => s + (Number.isFinite(a.cantidad) ? a.cantidad : 0), 0);
  const uObj = normalizarUnidadTexto(unidadObjetivo ?? '');
  const mismaUnidad = asignado.every((a) => normalizarUnidadTexto(a.unidad ?? '') === uObj);
  if (objetivo == null || !mismaUnidad) return { asignado: Math.round(total * 100) / 100, faltante: null, mismaUnidad };
  const falta = Math.max(0, objetivo - total);
  return { asignado: Math.round(total * 100) / 100, faltante: Math.round(falta * 100) / 100, mismaUnidad };
}
